import { type WindowLayout } from "core/WindowLayout";
import { StickyElementPosition } from "core/Controls/StickyElementPosition";
import { EventListener } from "silver";

type StickyElementChangeEvent = {
	fixed: boolean;
	bottom: boolean;
};

function setRect(element: HTMLElement, rect: Partial<Record<"top"|"left"|"width"|"height", number>>): void {
	element.style.left = rect.hasOwnProperty("left") ? `${rect.left}px` : "";
	element.style.top = rect.hasOwnProperty("top") ? `${rect.top}px` : "";
	element.style.width = rect.hasOwnProperty("width") ? `${rect.width}px` : "";
	element.style.height = rect.hasOwnProperty("height") ? `${rect.height}px` : "";
}

export class StickyElement {

	public readonly onChange = EventListener.create<StickyElementChangeEvent>("change");

	private stuckToTop?: boolean;
	private attachedToBottom?: boolean;
	private clone: HTMLElement;
	private position: StickyElementPosition;

	constructor(
		public readonly windowLayout: WindowLayout,
		public readonly element: HTMLElement,
		private stickyTopValue: number = 0,
		simpleStyle: boolean = true
	) {
		this.element.classList.add("sticky");
		if (simpleStyle) {
			this.element.classList.add("-simpleStyle");
		}
		this.clone = this.cloneNode();
		this.position = new StickyElementPosition(this.element, this.stickyTop);
		this.position.onParentNodeScroll(() => this.update(true));
		this.windowLayout.onUpdate(() => this.update(true));
		this.windowLayout.onScroll(() => this.update());

		this.windowLayout.doUpdate();
	}

	public get stickyTop(): number {
		return this.stickyTopValue;
	}

	public set stickyTop(value: number) {
		this.stickyTopValue = value;
		this.update(true);
	}

	public get height(): number {
		return (this.clone.parentNode ? this.clone.offsetHeight : this.element.offsetHeight);
	}

	private cloneNode(): HTMLElement {
		const clone = this.element.cloneNode(true) as HTMLElement;
		clone.classList.remove("-fixed", "-bottom");
		setRect(clone, { });
		clone.classList.add("-clone");
		clone.setAttribute("aria-hidden", "true");
		Array.from(clone.querySelectorAll("[id]")).forEach(element => {
			element.removeAttribute("id"); // the clone does not need these (please don't style on ID)
		});

		return clone;
	}

	public updateClone(): void {
		if (this.clone.parentNode) {
			this.clone.parentNode.removeChild(this.clone);
		}
		this.element.classList.remove("-fixed");
		this.clone = this.cloneNode();
		this.update(true);
	}

	public update(force: boolean = false): void {
		if (force) {
			this.position.recalculate(this.clone.parentNode ? this.clone : this.element, this.stickyTop);
		}
		if (!this.element.isConnected) {
			return;
		}

		const shouldStickToTop = this.position.topIsAboveViewport();
		const shouldAttachToBottom = shouldStickToTop && this.position.bottomIsAboveViewport();

		if (shouldStickToTop && !shouldAttachToBottom) {
			this.element.style.marginLeft = `${-1 * this.position.containerScrollX}px`;
		} else {
			this.element.style.marginLeft = "";
		}
		if (shouldStickToTop === this.stuckToTop && shouldAttachToBottom === this.attachedToBottom && !force) {
			return;
		}
		const parent = this.element.parentNode;
		if (!parent) {
			throw new Error("No parent");
		}
		this.stuckToTop = shouldStickToTop;
		this.attachedToBottom = shouldAttachToBottom;
		this.element.classList.toggle("-fixed", this.stuckToTop);
		this.element.classList.toggle("-bottom", this.attachedToBottom);
		if (shouldStickToTop) {
			parent.insertBefore(this.clone, this.element);
			const { left, width, height } = this.clone.getBoundingClientRect();
			if (shouldAttachToBottom) {
				setRect(this.element, { width, height, top: this.position.containerDocumentBottom - height - this.position.documentTop });
			} else {
				setRect(this.element, { left: left + window.scrollX, width, height, top: this.stickyTop });
			}
		} else {
			if (this.clone.parentNode) {
				this.clone.parentNode.removeChild(this.clone);
			}
			setRect(this.element, { });
		}
		this.onChange.trigger({ fixed: shouldStickToTop, bottom: shouldAttachToBottom });
	}
}
